<template>
  <div class="chat-bg">
    <div class="chat-wrapper" style="max-width: 1400px; margin: 0 auto;">
      <v-container
        class="pt-0"
        fluid
        v-bind:class="{
          'conversation-selected': selectedConversation !== null
        }"
      >
        <v-row>
          <v-col cols="12" lg="12" md="12" sm="12" style="padding: 0">
            <div v-if="!loading" class="chat-left">
              <div class="chat-header">
                <div
                  style="display: flex; flex-direction: row; padding: 20px 25px 20px 40px;"
                >
                  <div>
                    <div
                      style="font-size: 18px; align-items: center; font-weight: bold;"
                    >
                      {{ organization ? organization.name : "Chat" }}
                    </div>
                    <div style="font-size: 13px; margin-top: 5px; ">
                      <v-progress-circular
                        v-if="waiting"
                        :size="12"
                        class="mr-1"
                        indeterminate
                        style="margin-top: -3px;"
                        width="1"
                      />
                      {{ waiting ? "" : conversationsForOrganization.length }}
                      CONVERSATIONS
                    </div>
                  </div>

                  <div
                    style="display: flex; flex: 1; align-items: center; justify-content: flex-end;"
                  >
                    <v-btn
                      class="text-none"
                      color="primary"
                      elevation="0"
                      fab
                      small
                      v-on:click="showNewDMScreen"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
              <div id="chat-conversations" class="chat-body">
                <div
                  v-for="item in conversationsForOrganization"
                  :id="item.sid"
                  :key="item.sid"
                >
                  <ChatConversationCard
                    :key="item.sid"
                    :conversation="item"
                    :refreshedExcerpt="refreshedExcerpts[item.sid]"
                    :language="language"
                    :selected="selectedConversation === item"
                    v-on:selectConversation="selectConversation"
                  />
                </div>
              </div>
            </div>
            <div v-if="!loading" class="chat-right">
              <div
                v-if="!selectedConversation"
                class="chat-header chat-header-right"
              >
                <!-- this is a necessary filler -->
              </div>
              <ChatConversation
                v-if="selectedConversation !== null"
                :key="selectedConversation.sid"
                :chatClient="chatClient"
                :conversation="selectedConversation"
                :language="language"
                :limitedFunctionality="limitedFunctionality"
                :onClose="
                  () => {
                    this.selectedConversation = null;
                  }
                "
                :user="user"
              />
              <NewDMView
                v-if="showNewConversationScreen"
                :targetOrganization="
                  $route.params.orgId ? $route.params.orgId : null
                "
                :targetUsers="[]"
                @closeDialog="showNewConversationScreen = false"
                @fetchConversations="openNewConversation"
                @waitingOff="waiting = false"
              />
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>
<script>
import Vue from "vue";
import axios from "axios";
import { Client } from "@twilio/conversations";
import ChatConversation from "@/components/Chat/ChatConversation.vue";
import ChatConversationCard from "@/components/Chat/ChatConversationCard.vue";
import NewDMView from "@/components/Chat/NewDMView.vue";
import { bus } from "@/main";
import {
  isPushDiagnostic,
  refreshExcerpts,
  sortConversations
} from "@/helpers/chat";
import { mapGetters } from "vuex";
import GetterTypes from "@/store/GetterTypes";
import { getModule } from "vuex-module-decorators";
import ChatModule from "@/views/Chat/ChatModule";
import { api } from "@/helpers/ApiClient";
import goTo from "vuetify/lib/services/goto";

export default Vue.extend({
  name: "Chat",
  metaInfo: {
    title: "Chat"
  },
  components: {
    ChatConversation,
    ChatConversationCard,
    NewDMView
  },
  data() {
    return {
      module: getModule(ChatModule, this.$store),
      loading: false,
      waiting: false,
      organization: null,
      conversations: [],
      // sid -> {text, index, author} for excerpts the reconciler had to repair. Empty in the
      // normal case, where the excerpt on the conversation itself is current.
      refreshedExcerpts: {},
      message: "",
      chatClient: null,
      selectedConversation: null,
      showNewConversationScreen: false,
      language: "en",
      languages: [
        { code: "", name: "Off" },
        { code: "en", name: "English" },
        { code: "es", name: "Spanish" },
        { code: "fr", name: "French" },
        { code: "ar", name: "Arabic" },
        { code: "zh-CN", name: "Chinese" },
        { code: "hr", name: "Croatian" },
        { code: "cs", name: "Czech" },
        { code: "da", name: "Danish" },
        { code: "nl", name: "Dutch" },
        { code: "en", name: "English" },
        { code: "fr", name: "French" },
        { code: "de", name: "German" },
        { code: "haw", name: "Hawaiian" },
        { code: "he", name: "Hebrew" },
        { code: "hi", name: "Hindi" },
        { code: "hu", name: "Hungarian" },
        { code: "ga", name: "Irish" },
        { code: "it", name: "Italian" },
        { code: "ja", name: "Japanese" },
        { code: "ko", name: "Korean" },
        { code: "no", name: "Norwegian" },
        { code: "fa", name: "Persian" },
        { code: "pl", name: "Polish" },
        { code: "pt", name: "Portuguese" },
        { code: "ru", name: "Russian" },
        { code: "sm", name: "Samoan" },
        { code: "sr", name: "Serbian" },
        { code: "sk", name: "Slovak" },
        { code: "es", name: "Spanish" },
        { code: "sv", name: "Swedish" },
        { code: "tr", name: "Turkish" },
        { code: "vi", name: "Vietnamese" }
      ]
    };
  },
  async mounted() {
    const response = await axios.get(`/rest-api/chat/auth-token`);
    const token = response.data;

    this.chatClient = await new Client(token);
    this.chatClient.on("conversationUpdated", this.handleConversationUpdated);

    // if no orgId in route, then fetch for fan
    if (!this.$route.params.orgId) {
      await this.fetchConversations();
    }
  },
  // beforeDestroy, not unmounted: unmounted is the Vue 3 name and never fired here, so every visit
  // to this view added another conversationUpdated handler that outlived it.
  beforeDestroy() {
    if (this.chatClient) {
      this.chatClient.off(
        "conversationUpdated",
        this.handleConversationUpdated
      );
    }
  },
  methods: {
    handleConversationUpdated({ conversation }) {
      // The SDK emits {conversation, updateReasons}, not the conversation -- read as a conversation
      // its sid was undefined, so nothing ever matched. map() also returns a new array rather than
      // mutating, so its result has to be assigned.
      this.conversations = sortConversations(
        this.conversations.map(c =>
          c.sid === conversation.sid ? conversation : c
        )
      );
    },
    async selectConversationById(id) {
      if (this.conversations && this.conversations.length > 0 && id) {
        for (const conv of this.conversations) {
          if (id === conv.sid || id === conv.uniqueName) {
            this.showNewConversationScreen = false;
            this.selectedConversation = conv;

            // this is the only place we set the selectedConversation programmatically, so we may need
            // to scroll the conversation card into visibility
            const scrollTarget = "#" + conv.sid;
            const scrollOptions = {
              duration: 500,
              container: "#chat-conversations"
            };
            setTimeout(() => {
              goTo(scrollTarget, scrollOptions);
            }, 10);

            this.waiting = false;
            this.loading = false;
            return;
          }
        }
      } else {
        this.showNewConversationScreen = false;
        this.selectedConversation = null;
        this.waiting = false;
        this.loading = false;
      }
    },
    async openNewConversation(sid) {
      await this.fetchConversations();
      const conversation = this.conversations.find(conv => conv.sid === sid);
      this.selectConversation(conversation);
      bus.$emit("loadingOff");
    },
    async fetchConversations() {
      this.waiting = true;
      this.conversations = [];
      const page = await this.chatClient.getSubscribedConversations();
      await this.processPageOfConversations(page);
      const sid = this.$route.params.conversationSid;
      if (sid) {
        this.selectConversationById(sid);
      }
      this.waiting = false;

      // Deliberately after waiting is cleared: the list renders from the excerpts already on the
      // conversations, and this only repairs the ones the webhook missed. Awaiting it before showing
      // the list would make every load wait on a request that usually has nothing to do.
      this.reconcileExcerpts();
    },

    /**
     * Repair excerpts that fell behind their conversation's newest message. Sends only the
     * mismatches, so a healthy webhook means no request at all.
     */
    async reconcileExcerpts() {
      const repaired = await refreshExcerpts(this.conversations);
      if (Object.keys(repaired).length === 0) return;

      // Replaced rather than merged into: a second load should not keep serving a repair from the
      // first one, since by then the attributes on the conversation are authoritative again.
      this.refreshedExcerpts = repaired;
    },
    async processPageOfConversations(page) {
      page.items.forEach(conversation => {
        // Filtered on the way in rather than in the list computed, so it is excluded from everything
        // downstream at once: the list, the count beside the heading, excerpt reconciliation, and
        // selection by sid from the route.
        if (
          isPushDiagnostic(
            conversation,
            this.user && this.user.pushDiagnosticConversationSid
          )
        ) {
          return;
        }
        this.conversations.push(conversation);
      });
      if (page.hasNextPage) {
        page = await page.nextPage();
        await this.processPageOfConversations(page);
      } else {
        // they're loaded, so sort them
        this.conversations = sortConversations(this.conversations);
      }
    },

    showNewDMScreen() {
      if (this.limitedFunctionality) {
        bus.$emit("showExpiredSubscription");
      } else {
        this.waiting = true;
        this.selectedConversation = null;
        this.showNewConversationScreen = true;
      }
    },

    selectConversation(conversation) {
      const orgId = this.$route.params.orgId;
      const teamId = this.$route.params.teamId;
      const params = { orgId, teamId };
      if (conversation) {
        params.conversationSid = conversation.sid;
      }
      // edge case, but, make sure user did not navigate away from Chat before this was called
      const routeName = "Chat";
      if (this.$route.name === routeName && conversation) {
        this.$router.push({
          name: "Chat",
          params: params
        });
      }
    },
    closeConversation() {
      this.selectedConversation = null;
    }
  },
  computed: {
    conversationsForOrganization() {
      const orgId = this.$route.params.orgId;
      let filtered = this.conversationsSorted;

      if (orgId) {
        filtered = filtered.filter(c => c.attributes.organizationId === orgId);
      }
      return filtered;
    },
    conversationsSorted() {
      // Uses the shared helper so the order matches the date shown on each card. The inline sort
      // this replaces fell back to 0 when a conversation had no lastMessage, which pushed those to
      // the bottom while the card still displayed dateUpdated -- an order that contradicted the
      // dates on screen. It also sorted this.conversations in place, and a computed that mutates
      // its own dependency does not recompute when the Twilio SDK updates a conversation.
      return sortConversations(this.conversations);
    },
    ...mapGetters({
      user: GetterTypes.GET_USER,
      limitedFunctionality: GetterTypes.GET_ORG_HAS_LIMITED_FUNCTIONALITY
    })
  },
  watch: {
    "$route.params.conversationSid": {
      handler: function() {
        if (this.conversations) {
          const sid = this.$route.params.conversationSid;
          this.selectConversationById(sid);
        } else {
          this.selectConversationById(null);
        }
      },
      immediate: true
    },
    "$route.params.orgId": {
      handler: async function() {
        const orgId = this.$route.params.orgId;
        if (orgId) {
          const orgResp = await api.get(
            `/api/v1/organizationTeamSummary/${orgId}`
          );
          this.organization = orgResp.data;
          await this.fetchConversations();
          this.selectConversation(null);
        }
      },
      immediate: true
    }
  }
});
</script>
<style scoped>
.chat-bg {
  background: white !important;
  color: black !important;
  height: 100% !important;
}
.chat-wrapper {
  width: 100%;
  max-width: 1800px;
  border-left: 1px solid #ddd;
  border-right: 1px solid #ddd;
  margin: 0 auto;
  overflow: hidden;
  height: calc(
    100vh - (88px)
  ); /* viewport-height - (top bar + chat-header + chat-footer) */
}

.chat-container {
  display: flex;
}

.chat-left {
  width: 100%;
  float: left;
  overflow: hidden;
}

.conversation-selected .chat-left {
  width: 0;
  overflow: hidden;
}

.chat-right {
  width: 100%;
  overflow: hidden;
}

.chat-left {
  height: 100%;
}

.chat-left .chat-body {
  height: calc(
    100vh - (115px + 88px)
  ); /* viewport-height - (border + top bar + chat-header) */
  padding-bottom: 15px;
  overflow-y: auto;
  overflow-x: hidden;
  border-right: 1px solid #ddd;
}

.chat-header {
  overflow: hidden;
  height: 90px;
  border: 1px solid #ddd;
  border-left: none;
}

@media (min-width: 960px) {
  .chat-left {
    width: 40%;
  }

  .conversation-selected .chat-left {
    width: 40% !important;
  }

  .chat-right {
    width: 60%;
  }
}

@media (min-width: 1266px) {
  .chat-left {
    width: 30%;
  }

  .conversation-selected .chat-left {
    width: 30% !important;
  }

  .chat-right {
    width: 70%;
  }
}
</style>
